import {
  BookOpenIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import TextWinds from '../../common/TextWinds';
import { Button } from 'antd';
import { useContext, useMemo } from 'react';
import {
  AssignmentCategory,
  StudentAssignmentItem,
  StudentProgress,
} from '../../../constants/types/student/homework/type';
import { StudentClassDetailContext } from '../../../pages/student/StudentClassDetailContext';
import { StudentProfileContext } from '../../../pages/student/StudentProfileContext';

interface StudentAssignmentSidebarProps {
  classType: string;
  assignmentList: StudentAssignmentItem[];
  progress: StudentProgress | null;
  selectedCategory: AssignmentCategory;
  onCategoryChange: (category: AssignmentCategory) => void;
}

/**
 * 개편 LMS (학생) - 클래스 상세 페이지 > 콘텐츠 판넬 > 좌측 사이드바
 */
const StudentAssignmentSidebar = ({
  classType,
  assignmentList,
  progress,
  selectedCategory,
  onCategoryChange,
}: StudentAssignmentSidebarProps) => {
  const { classDetail } = useContext(StudentClassDetailContext);

  const { studentProfile } = useContext(StudentProfileContext);

  const menuItems = useMemo(() => {
    switch (classType) {
      case 'JEBSON':
        const jebsonCount = assignmentList.filter((assignment) =>
          ['EC', 'EF', 'ESB'].includes(assignment.type),
        ).length;

        const recitationCount = assignmentList.filter((assignment) =>
          ['JEBS_AI_RECITATION'].includes(assignment.type),
        ).length;

        return [
          {
            label: '젭스온',
            value: 'JEBSON' as const,
            count: jebsonCount,
            icon: BookOpenIcon,
          },
          {
            label: '인증제',
            value: 'RECITATION' as const,
            count: recitationCount,
            icon: ClipboardDocumentCheckIcon,
          },
        ];

      case 'WMW':
        return [
          {
            label: '젭스플',
            value: 'WMW' as const,
            count: assignmentList.length,
            icon: BookOpenIcon,
          },
        ];

      case 'HFHJ':
        return [
          {
            label: '젭스플',
            value: 'HFHJ' as const,
            count: assignmentList.length,
            icon: BookOpenIcon,
          },
        ];

      default:
        return [];
    }
  }, [classType, assignmentList]);

  const totalEarned = progress?.dalantCharacter.totalEarned ?? 0;
  const currentLevelAt = progress?.dalantCharacter.currentLevelAt ?? 0;
  const nextLevelAt = progress?.dalantCharacter.nextLevelAt ?? 0;

  const levelRange = nextLevelAt - currentLevelAt;

  const progressRate =
    levelRange > 0 ? ((totalEarned - currentLevelAt) / levelRange) * 100 : 0;

  return (
    <aside className="overflow-hidden rounded-xl border border-gray3 bg-white">
      <div className="overflow-hidden bg-white">
        {classDetail && (
          <div className="border-b border-gray3 px-5 py-5">
            <div className="flex items-center gap-4 xl:flex-col xl:items-stretch">
              <div className="min-w-0 flex-1 xl:w-full xl:flex-none">
                <TextWinds
                  type="title_h3"
                  color="gray9"
                  className="break-keep !text-base xl:!text-xl"
                >
                  {classDetail.course.name}
                </TextWinds>

                <TextWinds
                  type="content_body3"
                  color="gray7"
                  className="mt-1 block !text-xs xl:!text-sm"
                >
                  {classDetail.teacherName} 선생님
                </TextWinds>
              </div>

              <div className="w-[116px] shrink-0 overflow-hidden rounded-xl bg-gray1 xl:w-full xl:shrink">
                <img
                  src={classDetail.course.thumbnail}
                  alt={classDetail.course.name}
                  className="block aspect-[16/9] w-full object-cover"
                />
              </div>
            </div>
          </div>
        )}

        <div className="border-b border-gray3 px-5 py-5">
          <TextWinds
            type="title_h3"
            color="gray9"
            className="break-keep !text-base xl:!text-xl"
          >
            안녕, {studentProfile.name} 👋
          </TextWinds>

          <TextWinds
            type="content_body3"
            color="gray7"
            className="mt-1 block !text-xs xl:!text-sm"
          >
            오늘도 파이팅!
          </TextWinds>
        </div>

        {(classType === 'WMW' || classType === 'HFHJ') && progress && (
          <div className="border-b border-gray3 px-5 py-5">
            <div className="rounded-xl border border-purple2 bg-purple1/60 px-4 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <TextWinds type="caption1" className="font-bold text-purple5">
                    나의 달란트
                  </TextWinds>

                  <div className="mt-1 flex items-end gap-1">
                    <TextWinds
                      type="title_h3"
                      className="font-extrabold text-purple5"
                    >
                      {totalEarned.toLocaleString()}
                    </TextWinds>

                    <TextWinds
                      type="content_body3"
                      className="pb-0.5 font-bold text-purple5"
                    >
                      개
                    </TextWinds>
                  </div>
                </div>

                {progress.dalantCharacter.imageUrl && (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-sm">
                    <img
                      src={progress.dalantCharacter.imageUrl}
                      alt={
                        progress.dalantCharacter.levelName ?? '달란트 캐릭터'
                      }
                      className="h-full w-full object-contain p-1"
                    />
                  </div>
                )}
              </div>

              <div className="mt-4 rounded-lg bg-white/80 px-3 py-3">
                <div className="flex items-center justify-between gap-3">
                  <TextWinds
                    type="caption1"
                    color="gray7"
                    className="font-bold"
                  >
                    현재 레벨
                  </TextWinds>

                  <TextWinds type="caption1" className="font-bold text-purple5">
                    Lv.{progress.dalantCharacter.level ?? 0}
                  </TextWinds>
                </div>

                {progress && progress.dalantCharacter.nextLevelAt ? (
                  <>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray3">
                      <div
                        className="h-full rounded-full bg-purple5"
                        style={{
                          width: `${Math.min(100, Math.max(0, progressRate))}%`,
                        }}
                      />
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-2">
                      <TextWinds type="caption1" color="gray6">
                        다음 레벨까지
                      </TextWinds>

                      <TextWinds
                        type="caption1"
                        color="gray8"
                        className="font-bold"
                      >
                        {(nextLevelAt - totalEarned).toLocaleString()}개
                      </TextWinds>
                    </div>
                  </>
                ) : (
                  <TextWinds
                    type="caption1"
                    color="gray8"
                    className="mt-3 block text-center"
                  >
                    최고 레벨입니다.
                  </TextWinds>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className="grid gap-2 p-2 xl:flex xl:flex-col xl:gap-0 xl:divide-y xl:divide-gray3 xl:p-0"
        style={{
          gridTemplateColumns: `repeat(${menuItems.length}, minmax(0, 1fr))`,
        }}
      >
        {menuItems.map((item) => {
          const isSelected = selectedCategory === item.value;
          const Icon = item.icon;

          return (
            <Button
              key={item.value}
              type="text"
              className={[
                'flex h-10 w-full min-w-0 items-center justify-center rounded-lg !border-0 px-2 py-0',
                'xl:h-auto xl:w-full xl:justify-between xl:rounded-none xl:px-5 xl:py-4 xl:text-left',
                isSelected
                  ? '!bg-purple1/40'
                  : '!bg-white hover:!bg-gray0 active:!bg-gray1',
              ].join(' ')}
              onClick={() => onCategoryChange(item.value)}
            >
              <div className="flex min-w-0 items-center justify-center gap-1 xl:justify-start xl:gap-3">
                <Icon
                  className={[
                    'hidden h-5 w-5 shrink-0 xl:block',
                    isSelected ? 'text-purple5' : 'text-gray7',
                  ].join(' ')}
                />

                <TextWinds
                  type="caption1"
                  color={isSelected ? 'purple5' : 'gray8'}
                  className="min-w-0 truncate whitespace-nowrap font-bold xl:text-base"
                >
                  {item.label}
                </TextWinds>
              </div>

              <span
                className={[
                  'ml-1 flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-bold xl:ml-0 xl:h-7 xl:min-w-7 xl:px-2 xl:text-sm',
                  isSelected
                    ? 'bg-purple5 text-white'
                    : 'border border-purple5 bg-white text-purple5',
                ].join(' ')}
              >
                {item.count}
              </span>
            </Button>
          );
        })}
      </div>
    </aside>
  );
};

export default StudentAssignmentSidebar;
